import { computed, inject, Injectable, NgZone, OnDestroy, signal } from '@angular/core';
import { NavigationService } from '@core/navigation/services/navigation.service';
import { AppPathsMap } from '@core/routing/app-paths';
import { StorageService } from '@kerberos-compliance/compliance-shared-ui';
import { AppConstants } from '@src/app/app.constants';
import { fromEvent, merge, Subscription } from 'rxjs';
import { delay, filter } from 'rxjs/operators';
import { StorageServiceKeysMap } from '@/storage/types/storage.types';

const InactivityServiceConfig = {
  warningThresholdMs: 180 * 1000,
  checkIntervalMs: 1000,
} as const;

@Injectable({ providedIn: 'root' })
export class InactivityService implements OnDestroy {
  private readonly navigationService = inject(NavigationService);

  private readonly ngZone = inject(NgZone);

  private readonly storageService = inject(StorageService);

  private readonly idleTime = AppConstants.userInactiveTimeout;

  private readonly expiryTimestampSignal = signal<number | undefined>(undefined);

  private readonly remainingMsSignal = signal<number>(0);

  private readonly isTrackingSignal = signal<boolean>(false);

  private readonly lastActivityAtSignal = signal<number | undefined>(undefined);

  public readonly sessionExpiryTimestamp = this.expiryTimestampSignal.asReadonly();

  public readonly remainingMs = this.remainingMsSignal.asReadonly();

  public readonly isTracking = this.isTrackingSignal.asReadonly();

  public readonly lastActivityAt = this.lastActivityAtSignal.asReadonly();

  public readonly idleTimeMs = this.idleTime;

  public readonly warningThresholdMs = InactivityServiceConfig.warningThresholdMs;

  public readonly isWarningVisible = computed(() => {
    const remaining = this.remainingMsSignal();
    return remaining > 0 && remaining <= InactivityServiceConfig.warningThresholdMs;
  });

  private checkIntervalId?: ReturnType<typeof setInterval> = undefined;

  private activityEventsSubscription: Subscription | undefined = undefined;

  public ngOnDestroy(): void {
    this.stopTracking();
  }

  /**
   * Initiates tracking of user activity. Honors any in-flight session expiry persisted in storage
   * (so that reloads preserve the countdown) and never triggers a /logout redirect from init —
   * only the periodic tick can do that.
   *
   * @returns {void} Does not return any value.
   */
  public startTracking(): void {
    const storedExpiry = this.readStoredExpiry();
    const now = Date.now();
    const initialExpiry = storedExpiry && storedExpiry > now ? storedExpiry : now + this.idleTime;
    this.setExpiry(initialExpiry);
    this.remainingMsSignal.set(initialExpiry - now);

    const documentClick$ = fromEvent(document, 'click');
    const documentKeydown$ = fromEvent(document, 'keydown');

    const iframeClick$ = fromEvent(globalThis, 'blur').pipe(
      delay(0),
      filter(() => {
        const activeElement = document.activeElement;
        return !!activeElement && (activeElement instanceof HTMLIFrameElement || activeElement.tagName === 'IFRAME');
      }),
    );

    const iframeMessage$ = fromEvent<MessageEvent>(globalThis, 'message').pipe(
      filter((event) => {
        const origin = event.origin;
        return origin.includes('jotform.com') || origin.includes('jotfor.ms') || origin.includes('jotform.net');
      }),
    );

    const activityEvents$ = merge(documentClick$, documentKeydown$, iframeClick$, iframeMessage$).pipe(
      filter(() => !document.hidden),
    );

    this.activityEventsSubscription = activityEvents$.subscribe(() => {
      this.onActivity();
    });

    this.ngZone.runOutsideAngular(() => {
      this.checkIntervalId = globalThis.setInterval(() => {
        this.tick();
      }, InactivityServiceConfig.checkIntervalMs);
    });
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.isTrackingSignal.set(true);
  }

  /**
   * Stops the tracking process by unsubscribing from active event subscriptions, clearing intervals,
   * and removing event listeners related to tracking activities.
   *
   * @returns {void} This method does not return any value.
   */
  public stopTracking(): void {
    if (this.activityEventsSubscription) {
      this.activityEventsSubscription.unsubscribe();
      this.activityEventsSubscription = undefined;
    }
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = undefined;
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.isTrackingSignal.set(false);
  }

  /**
   * Resets the expiry clock to a full idle window. Used by the session-expiry banner's
   * "extend" CTA and guaranteed to keep the warning hidden afterward.
   *
   * @returns {void} This method does not return any value.
   */
  public extendSession(): void {
    const nextExpiry = Date.now() + this.idleTime;
    this.setExpiry(nextExpiry);
    this.remainingMsSignal.set(this.idleTime);
  }

  private tick(): void {
    const expiry = this.expiryTimestampSignal();
    if (!expiry) {
      return;
    }
    const remaining = expiry - Date.now();
    if (remaining <= 0) {
      this.ngZone.run(() => this.handleExpiry());
      return;
    }
    this.remainingMsSignal.set(remaining);
  }

  /**
   * Handles user activity events by updating the expiry time if the warning is not visible.
   * This method ensures that the countdown timer continues unless explicitly extended by user interaction.
   */
  private onActivity(): void {
    const now = Date.now();
    this.lastActivityAtSignal.set(now);
    if (this.isWarningVisible()) {
      return;
    }
    this.setExpiry(now + this.idleTime);
  }

  /**
   * Handles the expiration of a session by stopping tracking, clearing stored expiry data,
   * and navigating the user to the logout page if necessary.
   *
   * The method will first stop any active tracking and clear expiry data.
   * Then, it checks if the current route is among the excluded ones, such as the homepage,
   * logout, or backend-unavailable routes. If the current route is not excluded, it updates
   * the user's preferences with the last visited URL. Finally, it redirects the user to the logout page.
   */
  private handleExpiry(): void {
    this.stopTracking();
    this.clearStoredExpiry();

    const currentUrl = this.navigationService.getCurrentUrl();
    const isExcludedRoute =
      currentUrl === AppPathsMap.Homepage ||
      currentUrl.includes(`/${AppPathsMap.Logout}`) ||
      currentUrl.includes(`/${AppPathsMap.Error.BackendUnavailable}`);
    if (!isExcludedRoute) {
      this.storageService.set(StorageServiceKeysMap.PendingLastVisitedUrl, currentUrl);
    }
    void this.navigationService.goToLogout();
  }

  /**
   * Updates the expiry timestamp for the session and persists it to storage.
   *
   * @param {number} timestamp - The expiration time as a UNIX timestamp (in milliseconds).
   */
  private setExpiry(timestamp: number): void {
    this.expiryTimestampSignal.set(timestamp);
    this.storageService.set(StorageServiceKeysMap.SessionExpiryTimestamp, timestamp);
  }

  /**
   * Reads the stored session expiry timestamp from the storage service.
   * Validates that the retrieved value is a finite number.
   *
   * @returns {number | undefined} The session expiry timestamp if it is a valid finite number, otherwise undefined.
   */
  private readStoredExpiry(): number | undefined {
    const raw = this.storageService.get<number>(StorageServiceKeysMap.SessionExpiryTimestamp);
    return typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined;
  }

  /**
   * Clears the stored session expiry timestamp from the storage service
   * if it exists in the storage.
   */
  private clearStoredExpiry(): void {
    if (this.storageService.has(StorageServiceKeysMap.SessionExpiryTimestamp)) {
      this.storageService.remove(StorageServiceKeysMap.SessionExpiryTimestamp);
    }
  }

  /**
   * On tab visibility change, re-sync the remaining time. Does not auto-extend — if the
   * session lapsed while hidden, the next tick redirects to logout.
   *
   * @returns {void}
   */
  private handleVisibilityChange = (): void => {
    if (document.hidden) {
      return;
    }
    this.tick();
  };
}
